import { SUPPORT_EMAIL } from '@psyomics/ui-common';
import { Trans } from 'react-i18next';

import { Button } from '../../atoms/button/button';
import { Header } from '../../atoms/header/header';
import { Text } from '../../atoms/text/text';
import { BackLink } from '../../molecules/back-link/back-link';

type ChangePasswordAction = {
  readonly text: string;
  readonly onClick: () => void;
};

type AccountPageProps = {
  readonly username: string;
  readonly title: string;
  readonly onBack: () => void;
  readonly onSignOut: () => void;
  readonly signOutText: string;
  readonly changePassword?: ChangePasswordAction;
  readonly children?: React.ReactNode;
};

const UserInfo = ({ username, className }: { readonly username: string; readonly className?: string }) => (
  <div className={className}>
    <span>{username}</span>
  </div>
);

const ActionButtons = ({
  onChangePassword,
  onSignOut,
  signOutText,
  changePasswordText,
}: {
  readonly onChangePassword?: () => void;
  readonly onSignOut: () => void;
  readonly signOutText: string;
  readonly changePasswordText?: string;
}) => (
  <div>
    <div className="self-stretch inline-flex flex-col justify-start items-start gap-3 w-full">
      <Button appearance="secondary" className="w-full" onClick={() => onSignOut()}>
        {signOutText}
      </Button>
      {onChangePassword && changePasswordText && (
        <Button appearance="secondary" className="w-full" onClick={onChangePassword}>
          {changePasswordText}
        </Button>
      )}
    </div>
  </div>
);

function AccountPage({ username, title, onBack, onSignOut, signOutText, changePassword, children }: AccountPageProps) {
  return (
    <div className="relative flex justify-center">
      <div className="max-w-[680px] w-full mx-auto h-full pt-0">
        <div>
          <BackLink onBack={onBack} />
        </div>
        <Header className="pb-6" level="h2">
          {title}
        </Header>
        <UserInfo className="pb-2" username={username} />
        <ActionButtons
          changePasswordText={changePassword?.text}
          onChangePassword={changePassword?.onClick}
          onSignOut={onSignOut}
          signOutText={signOutText}
        />
        {children}
        <Text className="mt-8" size="lg" tag="p">
          <Trans
            components={{
              linkTag: (
                <a className="text-blue-500 underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
              ),
            }}
            i18nKey="page.account.support"
          />
        </Text>
      </div>
    </div>
  );
}

AccountPage.displayName = 'AccountPage';

export { AccountPage };
export type { AccountPageProps, ChangePasswordAction };
